import { Controller } from "../vendor/stimulus.js";
import { resizeBase64Image } from "../modules/resizeBase64Image.js";

export default class extends Controller {
  static targets = [
    "uploadField",
    "preview",
    "previewImage",
    "base64Input"
  ];

  static values = {
    imageMaxWidth:  { type: Number, default: 1024 },
    imageMaxHeight: { type: Number, default: 1024 }
  };

  connect() {
    this._onChange = (event) => this.onUploadFieldChange(event);
    this._onClearUpload = () => this.clearPreview();
    this.uploadFieldTarget.addEventListener("change", this._onChange);
    this.element.addEventListener("moodboard:clear-upload", this._onClearUpload);
  }

  disconnect() {
    this.uploadFieldTarget.removeEventListener("change", this._onChange);
    this.element.removeEventListener("moodboard:clear-upload", this._onClearUpload);
  }

  async onUploadFieldChange(event) {
    const fileInput = event.currentTarget;
    const file = fileInput.files?.[0];

    if (!file) return;

    // 1) Preview sofort aus originalem FileReader anzeigen
    const reader = new FileReader();
    reader.onload = (ev) => {
      const originalBase64 = ev.target.result;
      this.showPreview(originalBase64);
    };
    reader.readAsDataURL(file);

    // 2) Resize im Hintergrund durchführen
    try {
      const resizedBase64 = await this.handleImageInputAndResize(fileInput);
      this.writeHiddenBase64(resizedBase64);
    } catch (error) {
      console.error("Resize failed:", error);
    }
  }

  showPreview(base64) {
    if (this.hasPreviewImageTarget) {
      this.previewImageTarget.src = base64;
      if (this.hasPreviewTarget) {
        this.previewTarget.hidden = false;
        this.element.classList.add("has-preview");
      }
    }
    this.dispatchEvent("file-upload:preview", { base64 });
    this.resetFocus();
  }

  writeHiddenBase64(base64) {
    if (this.hasBase64InputTarget) {
      this.base64InputTarget.value = base64;
      const changeEvent = new Event("change", { bubbles: true });
      this.base64InputTarget.dispatchEvent(changeEvent);
    }
  }

  clearPreview() {
    if (this.hasUploadFieldTarget) {
      this.uploadFieldTarget.value = "";
    }

    if (this.hasBase64InputTarget) {
      this.base64InputTarget.value = "";
    }

    if (this.hasPreviewImageTarget) {
      this.previewImageTarget.src = "";
    }

    if (this.hasPreviewTarget) {
      this.previewTarget.hidden = true;
    }

    this.element.classList.remove("has-preview");
    this.dispatchEvent("file-upload:clear");
    this.resetFocus();
  }

  resetFocus() {
    const focusField = this.uploadFieldTarget.closest("form").querySelector("[autofocus]");
    focusField?.focus()
  }

  triggerUpload() {
    this.uploadFieldTarget.click();
  }

  handleFileSelect(event) {
    const file = event.target.files?.[0];
    if (!file) return;

    // Auto-submit the form
    const form = this.element.closest('form') || this.element;
    if (form.tagName === 'FORM') {
      this.submitForm({ currentTarget: form, preventDefault: () => {} });
    }
  }

  async submitForm(event) {
    event.preventDefault();

    const form = event.currentTarget;
    const formData = new FormData(form);
    const url = form.getAttribute('action');

    try {
      await fetch(url, {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });
    } catch (error) {
      console.error('Upload failed:', error);
    }
  }

  handleImageInputAndResize(fileInput) {
    const file = fileInput.files?.[0];
    if (!file) {
      return Promise.reject(new Error("No file selected."));
    }

    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = async (event) => {
        try {
          const base64 = event.target.result;
          const resizedBase64 = await resizeBase64Image(base64, {
            maxWidth: this.imageMaxWidthValue,
            maxHeight: this.imageMaxHeightValue,
            mimeType: "image/jpeg",
            quality: 0.85
          });
          resolve(resizedBase64);
        } catch (err) {
          reject(err);
        }
      };

      reader.onerror = (err) => {
        reject(err || new Error("Failed to read file."));
      };

      reader.readAsDataURL(file);
    });
  }

  dispatchEvent(name, detail = {}, target = this.element) {
    if (!target) return;
    const event = new CustomEvent(name, {
      detail,
      bubbles: true
    });
    target.dispatchEvent(event);
  }
}
